import { RefObject, useState, useCallback, useEffect } from 'react';

/**
 * 使用 CSS 实现的全屏功能（不使用浏览器原生全屏 API）
 * 通过 fixed 定位让容器覆盖整个视口
 * 
 * @param containerRef - 需要全屏的容器 ref
 * @param onFullScreenChange - 全屏状态变化回调
 */
export default function useCssFullScreen(
  containerRef: RefObject<HTMLElement | null>,
  onFullScreenChange?: (isFullScreen: boolean) => void,
) {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  // 进入全屏
  const requestFullScreen = useCallback(() => {
    if (containerRef.current) {
      setIsFullScreen(true);
      onFullScreenChange?.(true);
    }
  }, [containerRef, onFullScreenChange]);

  // 退出全屏
  const exitFullScreen = useCallback(() => {
    setIsFullScreen(false);
    onFullScreenChange?.(false);
  }, [onFullScreenChange]);

  // 切换全屏状态
  const toggleFullScreen = useCallback(() => {
    if (isFullScreen) {
      exitFullScreen();
    } else {
      requestFullScreen();
    }
  }, [isFullScreen, exitFullScreen, requestFullScreen]);

  // 监听 ESC 键退出全屏
  useEffect(() => {
    if (!isFullScreen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        exitFullScreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen, exitFullScreen]);

  return {
    isFullScreen,
    requestFullScreen,
    exitFullScreen,
    toggleFullScreen,
  };
}
